// The theatre diary for one day, as a doctor is allowed to see it.
//
// A doctor asking for an operation was choosing a date and a time blind — the
// OT schedule screen sits behind a permission a surgeon in clinic does not
// hold — so the "preference" they typed was a guess, and OT spent the day
// renegotiating it. This is the same diary, read only, beside the fields being
// filled in, so the guess becomes a choice.
//
// OTHER SURGEONS' PATIENTS ARE NOT HERE. The server sends theatre, slot and
// operating surgeon for a case that is not the caller's, and no patient name at
// all; this component could not display one if it tried. Redaction that happens
// in the browser is not redaction.
//
// Deliberately NOT a slot picker. The doctor still types the time they want —
// the diary informs that choice, it does not make it, because a free hour on
// this grid may be unusable for reasons a doctor cannot see (kit, staffing, a
// list that always overruns). OT still books the real time.

import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import moment from "moment";

import { fetchDoctorSchedule } from "../../ot/api/otApi";
import { formatIstTime, toIstWallClock } from "../../../utils/istDateTime";

const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const MINE = "#0F4C5C";
const OTHERS = "#94A3B8";

// The hours a theatre list usually occupies. A case or block outside them
// stretches the grid to take it in (windowOf), up to the whole day.
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const ROW_PX = 34;
// The theatre names' row. The hour rail shares it as an empty cell, so each
// hour label sits on its grid line.
const HEADER_PX = 18;

/**
 * Hours from the diary day's midnight (IST) to an instant: below 0 is the day
 * before, past 24 the day after. Null when there is no time.
 */
function hoursInto(date, iso) {
  const wall = toIstWallClock(iso); // "2026-09-08T00:30"
  if (!wall) return null;
  const [day, time] = wall.split("T");
  const [h, m] = time.split(":").map(Number);
  const days = Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86400000);
  return days * 24 + h + m / 60;
}

/**
 * The hours to draw: the usual theatre list, stretched to take in every case
 * and block of the day. A fixed 07:00-21:00 grid squashed a case that ran on to
 * 00:30 against its bottom edge. Never beyond the day itself.
 */
function windowOf(date, items) {
  let start = DAY_START_HOUR;
  let end = DAY_END_HOUR;
  items.forEach((item) => {
    const from = hoursInto(date, item.start_datetime);
    const to = hoursInto(date, item.end_datetime);
    if (from != null) start = Math.min(start, Math.floor(from));
    if (to != null) end = Math.max(end, Math.ceil(to));
  });
  return { start: Math.max(0, start), end: Math.min(24, end) };
}

/** Fraction down the grid, clamped to the window. */
function offsetOf(date, iso, win) {
  const hours = hoursInto(date, iso) ?? win.start;
  return Math.min(Math.max((hours - win.start) / (win.end - win.start), 0), 1);
}

function Block({ booking, top, height }) {
  const mine = booking.mine;
  return (
    <Box
      title={
        mine
          ? `${booking.patient_name || "Your case"} — ${
              booking.procedure_name || "procedure not named"
            }`
          : `Booked by ${booking.surgeon_name || "another surgeon"}`
      }
      sx={{
        position: "absolute",
        left: 3,
        right: 3,
        top: `${top * 100}%`,
        height: `${Math.max(height * 100, 3)}%`,
        minHeight: 18,
        borderRadius: "4px",
        px: 0.75,
        py: 0.25,
        overflow: "hidden",
        backgroundColor: mine ? MINE : "#F1F5F9",
        color: mine ? "#FFFFFF" : "#334155",
        border: `1px solid ${mine ? MINE : BORDER}`,
        fontSize: 10.5,
        lineHeight: 1.25,
      }}
    >
      <Box sx={{ fontWeight: 700 }}>
        {formatIstTime(booking.start_datetime)}
        {booking.duration_min ? ` · ${booking.duration_min}m` : ""}
      </Box>
      <Box sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
        {mine
          ? booking.patient_name || "Your case"
          : booking.surgeon_name || "Booked"}
      </Box>
    </Box>
  );
}

export default function OtDaySchedule({ date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError("");
    try {
      setData(await fetchDoctorSchedule(date));
    } catch (err) {
      // The diary is an aid, not a prerequisite. If it cannot be read the
      // doctor can still raise the request, so this says so quietly rather
      // than blocking the form. A refusal the server explains arrives on
      // `err.body`; a transport failure is summarised, never shown raw.
      setError(err?.body?.message || "The theatre diary could not be loaded. You can still send the request.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const rooms = data?.rooms || [];
  const bookings = data?.bookings || [];
  const blocks = data?.blocks || [];
  const win = windowOf(date, [...bookings, ...blocks]);
  const hours = win.end - win.start;

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          Theatre on {moment(date, "YYYY-MM-DD").format("ddd D MMM")}
        </Typography>
        <Typography sx={{ fontSize: 11, color: MUTED }}>view only</Typography>
        {loading && <CircularProgress size={12} />}
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 12, color: MUTED }}>{error}</Typography>
      ) : rooms.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {loading ? "Loading…" : "No theatres are configured."}
        </Typography>
      ) : (
        <>
          {/* Scrolls sideways when the theatres do not fit, rather than
              spilling past the card. */}
          <Box sx={{ overflowX: "auto" }}>
            <Box
              sx={{
                display: "grid",
                // The rail and every theatre share one header row and one grid
                // row, so each hour label sits on its line. The rail used to
                // start level with the theatre names, which put every label
                // above its line: an 08:00 case read as 09:00 (QA C2).
                gridTemplateColumns: `38px repeat(${rooms.length}, minmax(84px, 1fr))`,
                gridTemplateRows: `${HEADER_PX}px ${hours * ROW_PX}px`,
                columnGap: 0.5,
                rowGap: "2px",
                pb: 1, // the last hour's label hangs below the grid
              }}
            >
              <Box />
              {rooms.map((room) => (
                <Typography
                  key={`name-${room.id}`}
                  title={room.name}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    lineHeight: `${HEADER_PX}px`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {room.name}
                </Typography>
              ))}

              {/* The hour rail. */}
              <Box sx={{ position: "relative" }}>
                {Array.from({ length: hours + 1 }, (_, i) => (
                  <Typography
                    key={i}
                    sx={{
                      position: "absolute",
                      top: `${(i / hours) * 100}%`,
                      fontSize: 9.5,
                      color: MUTED,
                      transform: "translateY(-50%)",
                    }}
                  >
                    {String(win.start + i).padStart(2, "0")}:00
                  </Typography>
                ))}
              </Box>

              {rooms.map((room) => {
                const mine = bookings.filter((b) => b.operating_room_id === room.id);
                const unavailable = blocks.filter((b) => b.operating_room_id === room.id);
                return (
                  <Box
                    key={room.id}
                    sx={{
                      position: "relative",
                      border: `1px solid ${BORDER}`,
                      borderRadius: "4px",
                      backgroundImage: `repeating-linear-gradient(${BORDER} 0 1px, transparent 1px ${ROW_PX}px)`,
                    }}
                  >
                    {unavailable.map((blk, i) => {
                      const top = offsetOf(date, blk.start_datetime, win);
                      return (
                        <Box
                          key={`blk-${i}`}
                          title="Theatre unavailable"
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: `${top * 100}%`,
                            height: `${Math.max(
                              (offsetOf(date, blk.end_datetime, win) - top) * 100,
                              2,
                            )}%`,
                            backgroundImage:
                              "repeating-linear-gradient(45deg, #E2E8F0 0 4px, transparent 4px 8px)",
                          }}
                        />
                      );
                    })}
                    {mine.map((b) => {
                      const top = offsetOf(date, b.start_datetime, win);
                      return (
                        <Block
                          key={b.booking_id}
                          booking={b}
                          top={top}
                          height={offsetOf(date, b.end_datetime, win) - top}
                        />
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, mt: 1, alignItems: "center", flexWrap: "wrap" }}>
            <LegendSwatch colour={MINE} label="Yours" />
            <LegendSwatch colour={OTHERS} label="Another surgeon" />
            <Typography sx={{ fontSize: 10.5, color: MUTED }}>
              Other surgeons' patients are not shown.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

function LegendSwatch({ colour, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: colour }} />
      <Typography sx={{ fontSize: 10.5, color: MUTED }}>{label}</Typography>
    </Box>
  );
}
